import { cn, focusHaloFlush, panelMotion, press, type ThemeChoice, useTheme } from "@pax/ui";
import { Monitor, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { Soon } from "@/components/Soon";
import { type Arrangement, chooseArrangement, useArrangement } from "@/shell/arrangement";
import { Group, Segmented, SettingRow } from "./parts";

export const THEMES = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
] as const satisfies { value: ThemeChoice; label: string; icon: typeof Sun }[];

/** A row of choices drawn as small previews of the screen, with the chosen one ringed. */
function PreviewChoices<Value extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly { value: Value; label: string; preview: ReactNode }[];
  value: Value;
  onChange: (value: Value) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {options.map(option => {
        const chosen = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={chosen}
            onClick={() => onChange(option.value)}
            className={cn("group flex flex-col gap-2 rounded-2xl text-left", press, focusHaloFlush)}
          >
            <span
              className={cn(
                "flex h-20 overflow-hidden rounded-xl ring-1",
                chosen
                  ? "ring-2 ring-foreground"
                  : "ring-foreground/10 group-hover:ring-foreground/25",
                panelMotion,
              )}
            >
              {option.preview}
            </span>
            <span
              className={cn(
                "px-1 text-xs",
                chosen ? "font-medium text-foreground" : "text-muted-foreground",
              )}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** A screen in one theme: a title, a card, and a line. */
function ThemeScreen({ dark }: { dark: boolean }) {
  return (
    <span
      className={cn(
        "flex flex-1 flex-col gap-1.5 p-2.5",
        dark ? "bg-neutral-900" : "bg-neutral-100",
      )}
    >
      <span className={cn("h-1.5 w-8 rounded-full", dark ? "bg-white/40" : "bg-black/30")} />
      <span className={cn("flex-1 rounded-md", dark ? "bg-white/10" : "bg-black/[0.07]")} />
      <span className={cn("h-1.5 w-5 rounded-full", dark ? "bg-white/20" : "bg-black/15")} />
    </span>
  );
}

const THEME_PREVIEWS = THEMES.map(theme => ({
  value: theme.value,
  label: theme.label,
  preview:
    theme.value === "system" ? (
      <>
        <ThemeScreen dark={false} />
        <ThemeScreen dark />
      </>
    ) : (
      <ThemeScreen dark={theme.value === "dark"} />
    ),
}));

/** The ways on a small screen: three short lines. */
function WayLines({ across = false }: { across?: boolean }) {
  return (
    <span className={cn("flex gap-1", across ? "flex-row items-center" : "flex-col")}>
      {["w-6", "w-5", "w-7"].map(width => (
        <span key={width} className={cn("h-1.5 rounded-full bg-foreground/25", width)} />
      ))}
    </span>
  );
}

const ARRANGEMENTS = [
  {
    value: "sidebar",
    label: "Sidebar",
    preview: (
      <span className="flex flex-1 gap-1.5 bg-foreground/[0.04] p-1.5">
        <span className="flex w-1/4 flex-col gap-1.5 p-1">
          <WayLines />
        </span>
        <span className="flex-1 rounded-md bg-background ring-1 ring-foreground/10" />
      </span>
    ),
  },
  {
    value: "bar",
    label: "Top bar",
    preview: (
      <span className="flex flex-1 flex-col gap-1.5 bg-foreground/[0.04] p-1.5">
        <span className="px-1 pt-0.5">
          <WayLines across />
        </span>
        <span className="flex-1 rounded-md bg-background ring-1 ring-foreground/10" />
      </span>
    ),
  },
] as const satisfies { value: Arrangement; label: string; preview: ReactNode }[];

/**
 * The theme, as previews when there's room and a segmented control on a phone, and on a desktop,
 * whether the ways sit in a sidebar or a bar across the top.
 */
export function AppearanceSection({ phone }: { phone: boolean }) {
  const { choice, chooseTheme } = useTheme();
  const arrangement = useArrangement();
  return (
    <div className="flex flex-col gap-6">
      <Group label="Theme">
        {phone ? (
          <div className="p-1">
            <Segmented label="Theme" options={THEMES} value={choice} onChange={chooseTheme} />
          </div>
        ) : (
          <div className="p-2">
            <PreviewChoices options={THEME_PREVIEWS} value={choice} onChange={chooseTheme} />
          </div>
        )}
      </Group>
      {phone ? null : (
        <Group label="Navigation">
          <div className="p-2">
            <PreviewChoices
              options={ARRANGEMENTS}
              value={arrangement}
              onChange={chooseArrangement}
            />
          </div>
        </Group>
      )}
      <Group>
        <SettingRow title="Reduce motion" muted>
          <Soon />
        </SettingRow>
        <SettingRow title="Sounds" muted>
          <Soon />
        </SettingRow>
        {phone ? (
          <SettingRow title="Haptics" muted>
            <Soon />
          </SettingRow>
        ) : null}
      </Group>
    </div>
  );
}
